/**
 * A line-based diff of two texts, for reviewing prompts, skills and artifacts (FR-VERSION-7
 * for text: what a reviewer reads is which lines went and which came). Pure.
 *
 * Common leading and trailing lines are matched first; the rest by a longest common
 * subsequence. Past `MAXIMUM_COMPARED_CELLS` the middle is shown as all removed, then all
 * added, rather than spend the browser's time on it.
 */

export type DiffLineKind = "unchanged" | "added" | "removed";

export interface DiffLine {
  readonly kind: DiffLineKind;
  readonly text: string;
}

/** Unchanged lines folded between two changes; the lines are kept so the fold can be reopened. */
export interface CollapsedLines<Line> {
  readonly kind: "collapsed";
  /** The position of the first folded line in the whole diff. */
  readonly start: number;
  readonly lines: readonly Line[];
}

export type DiffRow<Line> = Line | CollapsedLines<Line>;

export const MAXIMUM_COMPARED_CELLS = 4_000_000;

export function lineDiff(oldText: string, newText: string): readonly DiffLine[] {
  const oldLines = splitLines(oldText);
  const newLines = splitLines(newText);
  let start = 0;
  while (start < oldLines.length && start < newLines.length && oldLines[start] === newLines[start]) {
    start += 1;
  }
  let oldEnd = oldLines.length;
  let newEnd = newLines.length;
  while (oldEnd > start && newEnd > start && oldLines[oldEnd - 1] === newLines[newEnd - 1]) {
    oldEnd -= 1;
    newEnd -= 1;
  }
  const unchanged = (text: string): DiffLine => ({ kind: "unchanged", text });
  return [
    ...oldLines.slice(0, start).map(unchanged),
    ...middleDiff(oldLines.slice(start, oldEnd), newLines.slice(start, newEnd)),
    ...oldLines.slice(oldEnd).map(unchanged),
  ];
}

/** True when the two texts differ in at least one line. */
export function hasChanges(lines: readonly DiffLine[]): boolean {
  return lines.some((line) => line.kind !== "unchanged");
}

/** Long runs of unchanged lines folded, keeping `context` lines next to each change. */
export function withCollapsedContext<Line extends { readonly kind: DiffLineKind }>(lines: readonly Line[], context: number): readonly DiffRow<Line>[] {
  const keep = lines.map((line) => line.kind !== "unchanged");
  lines.forEach((line, index) => {
    if (line.kind !== "unchanged") {
      for (let near = Math.max(0, index - context); near <= Math.min(lines.length - 1, index + context); near += 1) {
        keep[near] = true;
      }
    }
  });
  const rows: DiffRow<Line>[] = [];
  let folded: Line[] = [];
  let start = 0;
  lines.forEach((line, index) => {
    if (keep[index] === true) {
      if (folded.length > 0) {
        rows.push({ kind: "collapsed", start, lines: folded });
        folded = [];
      }
      rows.push(line);
    } else {
      if (folded.length === 0) start = index;
      folded.push(line);
    }
  });
  if (folded.length > 0) {
    rows.push({ kind: "collapsed", start, lines: folded });
  }
  return rows;
}

function splitLines(text: string): readonly string[] {
  return text === "" ? [] : text.replace(/\r\n?/g, "\n").split("\n");
}

function middleDiff(oldLines: readonly string[], newLines: readonly string[]): DiffLine[] {
  const removedAll = oldLines.map((text): DiffLine => ({ kind: "removed", text }));
  const addedAll = newLines.map((text): DiffLine => ({ kind: "added", text }));
  if (oldLines.length === 0 || newLines.length === 0 || oldLines.length * newLines.length > MAXIMUM_COMPARED_CELLS) {
    return [...removedAll, ...addedAll];
  }
  // common[i][j]: the longest common subsequence of oldLines[i..] and newLines[j..].
  const width = newLines.length + 1;
  const common = new Uint32Array((oldLines.length + 1) * width);
  for (let i = oldLines.length - 1; i >= 0; i -= 1) {
    for (let j = newLines.length - 1; j >= 0; j -= 1) {
      common[i * width + j] =
        oldLines[i] === newLines[j]
          ? (common[(i + 1) * width + j + 1] ?? 0) + 1
          : Math.max(common[(i + 1) * width + j] ?? 0, common[i * width + j + 1] ?? 0);
    }
  }
  const lines: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < oldLines.length && j < newLines.length) {
    const oldLine = oldLines[i] ?? "";
    const newLine = newLines[j] ?? "";
    if (oldLine === newLine) {
      lines.push({ kind: "unchanged", text: oldLine });
      i += 1;
      j += 1;
    } else if ((common[(i + 1) * width + j] ?? 0) >= (common[i * width + j + 1] ?? 0)) {
      lines.push({ kind: "removed", text: oldLine });
      i += 1;
    } else {
      lines.push({ kind: "added", text: newLine });
      j += 1;
    }
  }
  return [...lines, ...removedAll.slice(i), ...addedAll.slice(j)];
}
